import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SdvorIconComponent } from '@sdvor/shared/ui/v2';
import { YandexMapModule } from '@sdvor/yandex-maps/ui';
import { IconTypes } from '@shared/v2';
import { CustomPointOfService } from '@shop/const';
import { YmapsService } from '@yandex-maps/feature';
import { BehaviorSubject, filter, switchMap } from 'rxjs';
@Component({
  selector: 'sd-shop-map',
  imports: [AsyncPipe, YandexMapModule, SdvorIconComponent],
  templateUrl: './shop-map.component.html',
  styleUrls: ['./shop-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShopMapComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private ymapsService = inject(YmapsService);

  iconType = IconTypes;

  readonly MAX_ZOOM_IN = 20;
  readonly MAX_ZOOM_OUT = 3;

  @Input() shops: CustomPointOfService[];
  @Input() activeShopId: string;
  @Input() showBalloon = true;
  @Input() placemarksProps: (shop: CustomPointOfService) => ymaps.IPlacemarkTemplate;
  @Input() placemarkIconOptions?: (shop?: CustomPointOfService) => Record<string, any>;
  @Input() mapInstance: ymaps.Map;

  @Output() created = new EventEmitter<ymaps.Map>();
  @Output() placemarkClick = new EventEmitter<string>();

  shops$ = new BehaviorSubject<CustomPointOfService[] | undefined>(undefined);
  activeShopId$ = new BehaviorSubject<string | undefined>(undefined);
  shopGeoObjects$ = new BehaviorSubject<ymaps.Placemark[] | undefined>(undefined);
  shopClusterOptions$ = new BehaviorSubject<ymaps.IClustererOptions | undefined>(undefined);

  ngOnInit(): void {
    this.ymapsService.isLoaded$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        switchMap(() => this.shops$),
        filter(Boolean),
      )
      .subscribe((shops) => {
        const placemarks = shops
          .map((shop: CustomPointOfService) => {
            if (shop.geoPoint) {
              const defaultOptions = {
                balloonCloseButton: false,
                hideIconOnBalloonOpen: false,
                balloonOffset: [20, -45],
                hasBalloon: this.showBalloon,
                iconLayout: 'default#image',
                iconImageHref: 'assets/v2/images/ym/placemark.png',
                iconImageSize: [50, 65],
                iconImageOffset: [-20, -40],
              };

              const customOptions = this.placemarkIconOptions?.(shop) ?? {};

              const placemark = new ymaps.Placemark(
                [shop.geoPoint?.latitude, shop.geoPoint?.longitude],
                this.placemarksProps(shop),
                { ...defaultOptions, ...customOptions },
              );

              placemark.events.add('click', () => {
                this.placemarkClick.emit(shop.name);
              });

              return placemark;
            }
          })
          .filter((value): value is ymaps.Placemark => value !== undefined);

        this.shopGeoObjects$.next(placemarks);

        this.shopClusterOptions$.next({
          clusterIconLayout: ymaps.templateLayoutFactory.createClass(`
            <div style="
              position: relative;
              width: 45px;
              height: 45px;
            ">
              <div style="
                position: absolute;
                width: 45px;
                height: 45px;
                border-radius: 50%;
                background: white;
                border: 2px solid #F7A707;
                box-sizing: border-box;
                top: 0;
                left: 0;
              "></div>

              <div style="
                position: absolute;
                width: 30px;
                height: 30px;
                border-radius: 50%;
                background-color: #F7A707;
                top: calc(50% - 15px);
                left: calc(50% - 15px);
              "></div>

              <div style="
                position: absolute;
                color: #11100F;
                font-size: 16px;
                font-weight: 400;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 30px;
                text-align: center;
                line-height: 1;
                pointer-events: none;
              ">
                $[properties.geoObjects.length]
              </div>
            </div>
          `),
          clusterIconShape: { type: 'Circle', coordinates: [22, 22], radius: 22 },
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shops) {
      this.shops$.next(this.shops);
    }

    if (changes.activeShopId) {
      this.activeShopId$.next(this.activeShopId);
    }
  }

  onMapCreated(ymap: ymaps.Map): void {
    this.mapInstance = ymap;
    this.created.emit(ymap);
  }

  onMapZoom(zoomType: 'IN' | 'OUT'): void {
    if (this.mapInstance) {
      const currentZoom = this.mapInstance.getZoom();
      if (currentZoom < this.MAX_ZOOM_IN && zoomType === 'IN') {
        this.mapInstance.setZoom(currentZoom + 1, { duration: 300 });
      } else if (currentZoom > this.MAX_ZOOM_OUT) {
        this.mapInstance.setZoom(currentZoom - 1, { duration: 300 });
      }
    }
  }
}
